import React from 'react'
import { useNavigate } from 'react-router-dom'
import { Typography, TableContainer, Paper, Table, TableBody, TableRow, TableCell, Avatar, Box, Button, Dialog, DialogContent, LinearProgress } from '@mui/material'
import { QuestionFrame } from 'components/questions'
import { blue, green, grey, red } from '@mui/material/colors'
import { useGlobalState } from 'state'
import * as api from 'common/api'
import { QuestionPage, YesNoValue } from 'common/types'
import { QuestionPages } from 'common/consts'


type Page = QuestionPage & {
  progress: number
}

type PageGroup = {
  title: string
  pages: Page[]
}

export const QList = () => {
  const state = useGlobalState()
  const navigate = useNavigate()
  const [calcProgress, setCalcProgress] = React.useState(0)
  const [calcLoading, setCalcLoading] = React.useState(false)

  const basePages: Page[] = QuestionPages.map((row) => {
    return { ...row, progress: 0 }
  })

  const navPage = (page: Page) => {
    if (page.progress === -1) {
      return
    }
    navigate(page.to, {replace: false})
  }

  const a6_1: YesNoValue | null = React.useMemo(() => {
    const answer = state.app.getAnswer(6, 1)
    return answer === null ? null : answer.value
  }, [state.app.answers])

  const pages: Page[] = React.useMemo(() => {
    return basePages.map((row) => {
      if (state.app.excludePages.includes(row.q)) {
        row.progress = -1
      } else if (row.to === '/map') {
        const answer = state.app.getAnswerByKey('map')
        row.progress = 0
        if (answer !== null) {
          row.progress = answer.valid === true ? 100 : answer.value.lat !== undefined ? 50 : 0
        }
      } else if (row.q === 6 && a6_1 === null) {
        row.progress = 0
      } else if (row.q === 6 && a6_1 === 'いいえ') {
        row.progress = 100
      } else {
        for (let i = 1; i <= row.count; i++) {
          const answer = state.app.getAnswer(row.q, i)
          if (answer !== null && answer.valid === true) {
            row.progress = Math.ceil(row.progress + 100 / row.count)
            if (row.progress > 100) {
              row.progress = 100
            }
          }
        }
      }
      return row
    })
  }, [state.app.excludePages, state.app.answers])

  const pageGroups: PageGroup[] = React.useMemo(() => {
    const groups: PageGroup[] = []
    let title = ''
    pages.forEach((page) => {
      if (page.title !== title) {
        title = page.title
        groups.push({title: title, pages: [page]})
      } else {
        groups[groups.length - 1].pages.push(page)
      }
    })
    return groups
  }, [pages])

  const progressText = (page: Page) => {
    if (page.progress === -1) {
      return <Typography variant='body2' fontWeight={'bold'} color={grey[500]}>回答不要</Typography>
    } else if (page.progress >= 100) {
      return <Typography variant='body2' fontWeight={'bold'} color={green[500]}>回答済み</Typography>
    } else if (page.to === '/map') {
      return <Typography variant='body2' fontWeight={'bold'} color={red[600]}>{page.progress === 50 ? '再計算が必要です' : '未選択'}</Typography>
    } else {
      const color = page.progress <= 50 ? red[400] : blue[600]
      return <Typography variant='body2' fontWeight={'bold'} color={color}>未回答 {page.progress} %</Typography>
    }
  }

  const disabledCalc = React.useMemo(() => {
    return pages.filter((row) => row.progress !== -1 && row.progress < 100).length > 0
  }, [pages])

  React.useEffect(() => {
    if (calcLoading === false) {
      return
    }
    const timer = setInterval(() => {
      setCalcProgress((prevProgress) => {
        if (prevProgress >= 100) {
          clearInterval(timer)
          return 100
        } else {
          return prevProgress + 1
        }
      })
    }, 200)
    return () => {
      clearInterval(timer)
    }
  }, [calcLoading])

  const calcScorePoint = async () => {
    setCalcProgress(0)
    setCalcLoading(true)

    const answers = JSON.parse(JSON.stringify(state.app.answers))
    state.app.excludePages.forEach((q: number) => {
      Object.keys(answers).forEach((key: string) => {
        if (key.startsWith(`q${q}-`)) {
          delete answers[key]
        }
      })
    })

    if (a6_1 === 'いいえ') {
      delete answers['q6-2']
      delete answers['q6-3']
      delete answers['q6-4']
      delete answers['q6-5']
      delete answers['q6-6']
      delete answers['q6-7']
    }

    try {
      const params = answers
      const res = await api.post('/scorepoint', params)
      if (res === null) {
        alert('簡易算定に失敗しました')
        return
      }
      const scorepoint = res
      navigate('/score', {replace: false, state: {scorepoint}})
    } catch (e) {
      console.log(e)
    } finally {
      setCalcLoading(false)
    }
  }

  return (
    <QuestionFrame
      q={99}
      centerButton={<Button variant='contained' sx={{width: 120}} disabled={disabledCalc} onClick={calcScorePoint}>簡易算定</Button>}
    >
      <Dialog open={calcLoading}>
        <DialogContent>
          <Box sx={{ display: 'flex', alignItems: 'center', width: 300 }}>
            <Box sx={{ width: '100%', mr: 1 }}>
              <LinearProgress variant="determinate" value={calcProgress} />
            </Box>
            <Box sx={{ minWidth: 35 }}>
              <Typography
                variant="body2"
                sx={{ color: 'text.secondary' }}
              >{`${Math.round(calcProgress)}%`}</Typography>
            </Box>
          </Box>
          <Typography variant='body2' color={grey[600]}>スコア計算中...</Typography>
        </DialogContent>
      </Dialog>

      { pageGroups.map((group, index) => (
        <Box key={index} width={'100%'}>
          <Typography variant='subtitle1' fontWeight={'bold'} color={grey[700]} align='left' mt={index > 0 ? 3 : 0} mb={1}>
            { `【 ${group.title} 】`}
          </Typography>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableBody>
                {group.pages.map((row, index) => (
                  <TableRow
                    key={index}
                    sx={{ '&:last-child td, &:last-child th': { border: 0 }, cursor: 'pointer'}}
                    hover
                    onClick={() => navPage(row)}
                  >
                    <TableCell width={52}>
                      { row.q > 0 && <Avatar sx={{ bgcolor: green[400], width: 20, height: 20, fontSize: '0.7rem'}}>{row.q}</Avatar> }
                    </TableCell>
                    <TableCell width={180}>
                      <Typography variant='body2' align='left'>{row.q > 0 ? row.subtitle : ''}</Typography>
                      <Typography variant='caption' align='left' color={grey[700]}>{row.heading}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant='body2' align='left' whiteSpace={'pre-wrap'}>{row.content}</Typography>
                    </TableCell>
                    <TableCell align="right" sx={{minWidth: 180}}>
                      { progressText(row) }
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      ))}

    </QuestionFrame>
  )
}
